import {
  SaveWorkforceAccountSchema,
  WorkforceAccountSchema,
  type SaveWorkforceAccount,
} from "@emr/contracts";

export function createWorkforceAccountClient(options: {
  baseUrl: string;
  siteId: string;
  getAccessToken: () => Promise<string>;
  fetch?: typeof fetch;
}) {
  async function call(userId: string, command?: SaveWorkforceAccount, signal?: AbortSignal) {
    const parsedCommand = command ? SaveWorkforceAccountSchema.safeParse(command) : undefined;
    if (parsedCommand && !parsedCommand.success)
      throw new Error(
        "Check your profile details. Use a name of at least two characters and complete billing numbers.",
      );
    const url = new URL(`/v1/workforce/accounts/${encodeURIComponent(userId)}`, options.baseUrl);
    url.searchParams.set("siteId", options.siteId);
    const response = await (options.fetch ?? fetch)(url, {
      method: command ? "PUT" : "GET",
      cache: "no-store",
      credentials: "omit",
      headers: {
        Authorization: `Bearer ${await options.getAccessToken()}`,
        "Content-Type": "application/json",
        "x-site-id": options.siteId,
      },
      ...(parsedCommand?.success ? { body: JSON.stringify(parsedCommand.data) } : {}),
      ...(signal ? { signal } : {}),
    });
    if (!response.ok)
      throw new Error(
        response.status === 409
          ? "This profile changed. Your edits are retained. Reload the profile before saving again."
          : response.status === 403
            ? "You do not have permission to change this profile."
            : "The profile could not be loaded or saved. Check your connection and try again.",
      );
    const account = WorkforceAccountSchema.parse(await response.json());
    if (account.siteId !== options.siteId || (userId !== "me" && account.userId !== userId))
      throw new Error("The profile response did not match the selected person and clinic.");
    return account;
  }
  return {
    get: (userId = "me", signal?: AbortSignal) => call(userId, undefined, signal),
    save: (userId: string, command: SaveWorkforceAccount) => call(userId, command),
  };
}
